import { API_URL } from "@/constants";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ApiRequestOptions = {
  method?: string;
  body?: unknown;
  authToken?: string | null;
};

let authTokenGetter: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  authTokenGetter = getter;
}

export async function apiRequest<TResponse>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<TResponse> {
  const headers = new Headers({
    "Content-Type": "application/json",
  });

  const token =
    options.authToken === undefined
      ? await authTokenGetter?.()
      : options.authToken;
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_URL}${path}`, {
    method: options.method || "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const responseBody = (await response
    .json()
    .catch(() => undefined)) as
    | TResponse
    | { error?: string; details?: unknown }
    | undefined;
  const errorLike =
    responseBody && typeof responseBody === "object"
      ? (responseBody as { error?: string; details?: unknown })
      : undefined;

  if (!response.ok) {
    const message =
      typeof errorLike?.error === "string"
        ? errorLike.error
        : `Request failed with status ${response.status}`;

    throw new ApiError(message, response.status, errorLike?.details);
  }

  return responseBody as TResponse;
}
